import { Grid, Skeleton } from '@mui/material';
import AndroidIcon from '@mui/icons-material/Android';
import AppleIcon from '@mui/icons-material/Apple';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import {
  CardPlatformContainer,
  CardPlatformScoreContainer,
} from '../FullVariants/ExposeAppCard/ExposeAppCard.styles';
import { DynamicBadge } from '../../DynamicBadge';
import type { ImageProps } from 'next/image';

const platformIconSelector = {
  ios: AppleIcon,
  android: AndroidIcon,
  web: DesktopWindowsIcon,
};

export interface AppPlatformImageInfoProps {
  platforms?: Array<{
    platformName: 'ios' | 'android' | 'web' | undefined;
    platformTitle: string;
    score?: number;
  }>;
  thresholds?: Array<{
    imageUrl: string;
    minValue: number;
    maxValue: number;
  }>;
  isLoading?: boolean;
  FlexibleImageComponent?: React.ComponentType<ImageProps>;
}

const PlatformSkeleton = () => {
  return (
    <Grid item xs={12} container spacing={2}>
      {['ios', 'android', 'web'].map((i) => (
        <Grid item xs={4} key={i}>
          <Skeleton variant="rounded" height={68} width="100%" />
        </Grid>
      ))}
    </Grid>
  );
};

/**
 * AppPlatformImageInfo component from [MicroLibraryCoreUI](https://dev.azure.com/orcha/MicroLibraryTemplate/_git/MicroLibraryCoreUI)
 *
 * @property platforms - The platforms to display.
 * @property isLoading - Whether the platforms are loading.
 */
export const AppPlatformImageInfo = ({
  platforms,
  isLoading,
  thresholds,
  FlexibleImageComponent,
}: AppPlatformImageInfoProps) => {
  if (!platforms || platforms.length === 0) {
    return null;
  }
  if (!thresholds || thresholds.length === 0) {
    return null;
  }
  if (!FlexibleImageComponent) {
    return null;
  }
  if (isLoading) {
    return <PlatformSkeleton />;
  }

  return (
    <Grid item xs={12} container spacing={2}>
      {platforms.map((platform) => {
        const { platformName, score, platformTitle } =
          platform;
        const IconComponent = platformName
          ? platformIconSelector[platformName]
          : undefined;

        return (
          <Grid key={platformName} item xs={4}>
            <CardPlatformContainer>
              <CardPlatformScoreContainer
                data-testid={`app-card-score-${platformName}`}
              >
                <Grid container>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <DynamicBadge
                      height={32}
                      width={32}
                      FlexibleImageComponent={FlexibleImageComponent}
                      value={score ?? 0}
                      thresholds={thresholds}
                      translatedText={{ tooltipText: platformTitle }}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    {IconComponent && (
                      <IconComponent
                        titleAccess={platformTitle}
                        sx={{ fontSize: '17px', margin: 'auto' }}
                        color="primary"
                      />
                    )}
                  </Grid>
                </Grid>
              </CardPlatformScoreContainer>
            </CardPlatformContainer>
          </Grid>
        );
      })}
    </Grid>
  );
};
